import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const alerts = [
  {
    id: 1,
    type: 'critical',
    icon: 'AlertCircle',
    color: 'var(--color-destructive)',
    bg: 'bg-red-50',
    border: 'border-red-200',
    title: 'Inventory Stockout Risk',
    message: 'SKU-SH01 (Shampoo 400ml) projected to stockout in Visayas within 8 days. Current stock: 142 units. Daily consumption: 18 units.',
    territory: 'Visayas',
    time: '2 hours ago',
    dismissed: false,
  },
  {
    id: 2,
    type: 'warning',
    icon: 'TrendingDown',
    color: 'var(--color-warning)',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    title: 'Declining Account Cluster',
    message: '12 accounts in QC North show 3-consecutive-month order decline for Toothpaste and Mouthwash. Combined revenue at risk: ₱220,000/month.',
    territory: 'NCR',
    time: '5 hours ago',
    dismissed: false,
  },
  {
    id: 3,
    type: 'opportunity',
    icon: 'TrendingUp',
    color: 'var(--color-success)',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    title: 'Competitor Weakness Detected',
    message: 'Social listening and field reports indicate competitor distribution issues in Davao for Body Wash and Lotion. 34 accounts may be open to switching. Estimated opportunity: ₱480,000/month.',
    territory: 'Mindanao',
    time: '1 day ago',
    dismissed: false,
  },
  {
    id: 4,
    type: 'info',
    icon: 'Info',
    color: 'var(--color-primary)',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    title: 'Seasonal Demand Approaching',
    message: 'AI models predict 65% demand increase in Iloilo starting April 15 (fiesta season) for Deodorant and Facial Wash. Pre-positioning inventory recommended by April 8.',
    territory: 'Visayas',
    time: '2 days ago',
    dismissed: false,
  },
];

const TYPE_LABELS = {
  critical: { label: 'Critical', cls: 'bg-red-100 text-red-700 border-red-200' },
  warning: { label: 'Warning', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  opportunity: { label: 'Opportunity', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  info: { label: 'Info', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
};

export default function AlertsPanel() {
  const [dismissed, setDismissed] = useState([]);
  const [filter, setFilter] = useState('all');

  const visible = alerts?.filter((a) => !dismissed?.includes(a?.id) && (filter === 'all' || a?.type === filter));

  return (
    <div className="card p-4 lg:p-6 table-panel-depth">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">AI Pattern Alerts</h3>
          <p className="font-caption text-xs text-muted-foreground mt-0.5">{visible?.length} active alerts requiring attention</p>
        </div>
        <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
          <Icon name="Bell" size={14} color="var(--color-destructive)" strokeWidth={2} />
          <span className="font-data text-sm font-semibold text-red-600">{alerts?.filter((a) => !dismissed?.includes(a?.id))?.length}</span>
        </div>
      </div>
      {/* Filter tabs */}
      <div className="flex gap-1.5 flex-wrap mb-4 overflow-x-auto pb-1">
        {['all', 'critical', 'warning', 'opportunity', 'info']?.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-lg border font-caption text-xs font-medium transition-all flex-shrink-0 ${
              filter === f ? 'border-primary bg-blue-50 text-primary' : 'border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            {f?.charAt(0)?.toUpperCase() + f?.slice(1)}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        {visible?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon name="CheckCircle" size={32} color="var(--color-success)" strokeWidth={1.5} />
            <p className="font-caption text-sm text-muted-foreground mt-2">No alerts in this category</p>
          </div>
        )}
        {visible?.map((alert) => {
          const typeInfo = TYPE_LABELS?.[alert?.type];
          return (
            <div key={alert?.id} className={`border rounded-xl p-4 ${alert?.bg} ${alert?.border}`}>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5" style={{ width: 34, height: 34, background: `${alert?.color}20` }}>
                  <Icon name={alert?.icon} size={17} color={alert?.color} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-heading font-semibold text-sm text-foreground">{alert?.title}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${typeInfo?.cls}`}>{typeInfo?.label}</span>
                  </div>
                  <p className="font-body text-xs text-foreground leading-relaxed mb-2">{alert?.message}</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Icon name="MapPin" size={11} color="var(--color-muted-foreground)" strokeWidth={2} />
                      <span className="font-caption text-xs text-muted-foreground">{alert?.territory}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={11} color="var(--color-muted-foreground)" strokeWidth={2} />
                      <span className="font-caption text-xs text-muted-foreground">{alert?.time}</span>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <Button variant="ghost" size="xs" iconName="ExternalLink" iconPosition="left" iconSize={12}>
                        View
                      </Button>
                      <button
                        onClick={() => setDismissed((p) => [...p, alert?.id])}
                        className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-card transition-colors"
                        aria-label="Dismiss alert"
                      >
                        <Icon name="X" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}